/**
 * Ecom clickout — one linear flow:
 *
 *   capture → session count → track(eafc) [+ eafuc once per session]
 *   → ECOM_CLICKOUT signal (pixels) → BEFORE_NAVIGATE → navigate.
 *
 * `/dye` uses sendBeacon (survives navigation), so no deferral gymnastics are
 * needed before leaving the page.
 */

import { SdkEvent } from '../../core/events/bus';
import { AwaitFor } from '../../core/events/awaiter';
import type { SdkContext } from '../types';
import { ECOM_EVENTS, buildClickoutParams, type ClickoutPayload } from './events';
import { debug } from '../../util/log';

const SESS_CLICKS_KEY = 'kz_clickout_sess_clicks';
/** Set when the unique click (`eafuc`) has been sent this browser session (independent of click count). */
const SESS_UNIQUE_FIRED_KEY = 'kz_ecom_sess_unique_fired';

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function incrementSessionClicks(): number {
  const store = safeSessionStorage();
  if (!store) {
    return 1;
  }
  try {
    const raw = store.getItem(SESS_CLICKS_KEY);
    const count = raw ? parseInt(raw, 10) : 0;
    const next = Number.isNaN(count) ? 1 : count + 1;
    store.setItem(SESS_CLICKS_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}

function markUniqueClickFired(): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.setItem(SESS_UNIQUE_FIRED_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** Whether this session has not yet sent the unique click (`eafuc`). */
function shouldFireUniqueClick(): boolean {
  const store = safeSessionStorage();
  if (!store) return true;
  try {
    return store.getItem(SESS_UNIQUE_FIRED_KEY) !== '1';
  } catch {
    return true;
  }
}

export function _resetClickoutForTests(): void {
  const store = safeSessionStorage();
  if (store) {
    try {
      store.removeItem(SESS_CLICKS_KEY);
      store.removeItem(SESS_UNIQUE_FIRED_KEY);
    } catch {
      /* ignore */
    }
  }
}

export type EcomClickoutDispatchArgs = ClickoutPayload;

export interface EcomClickoutDispatchOptions {
  /** Navigate to the outbound URL. Default `true`; `false` for anchor clicks. */
  navigate?: boolean;
}

/**
 * Runs the full clickout flow, ending with navigation to the outbound URL
 * unless `opts.navigate` is `false`.
 *
 * @returns whether analytics were sent.
 */
export function dispatchEcomClickout(
  ctx: SdkContext,
  args: EcomClickoutDispatchArgs,
  opts: EcomClickoutDispatchOptions = {},
): boolean {
  const sessionClicks = incrementSessionClicks();
  const payload: ClickoutPayload = { ...args, sessionClicks };
  const params = buildClickoutParams(payload);
  const unique = shouldFireUniqueClick();

  debug('ecom clickout', {
    url: payload.url,
    unique,
    sessionClicks,
    events: unique ? [ECOM_EVENTS.click, ECOM_EVENTS.uniqueClick] : [ECOM_EVENTS.click],
    params,
  });

  ctx.track(ECOM_EVENTS.click, params);
  if (unique) {
    ctx.track(ECOM_EVENTS.uniqueClick, params);
    markUniqueClickFired();
  }

  ctx.emit(SdkEvent.ECOM_CLICKOUT, {
    url: payload.url,
    productId: payload.productId,
    advertiserCampaignId: payload.advertiserCampaignId,
    ecomProductMarket: payload.ecomProductMarket,
    unique,
    sessionClicks,
  });

  ctx.emit(SdkEvent.BEFORE_NAVIGATE, { url: payload.url });
  if (opts.navigate !== false && typeof window !== 'undefined') {
    window.location.href = payload.url;
  }

  return true;
}

/** Public `ecom.trackClickout` — waits for READY, then runs the flow. */
export async function trackEcomClickout(
  ctx: SdkContext,
  args: EcomClickoutDispatchArgs,
): Promise<boolean> {
  if (!ctx.isAwaitCompleted(AwaitFor.READY)) {
    await ctx.awaitFor(AwaitFor.READY);
  }
  return dispatchEcomClickout(ctx, args);
}
